<!-- @format -->

<template>
  <div>
    <div class="ui attached message">
      <h3>Suitability Analysis - Goal Risk Profile (GRP) - {{ currency }}</h3>
    </div>

    <div class="ui attached segment">
      <div class="ui four tin ordered steps">
        <a
          href="#"
          @click.prevent="changeRiskStep('riskNeed')"
          :class="
            (getRiskNeedResponse ? 'completed' : '') +
            ' step ' +
            (riskStep == 'riskNeed' ? 'active' : '')
          "
        >
          <div class="content">
            <div class="title">Risk Need</div>
            <div class="description" v-if="getRiskNeedResponse">
              {{ getRiskNeedResponse.riskProfile }}
            </div>
          </div>
        </a>
        <a
          href="#"
          @click.prevent="changeRiskStep('riskAbility')"
          :class="
            (getRiskAbilityResponse ? 'completed ' : '') +
            ' step ' +
            (riskStep == 'riskAbility' ? 'active' : '')
          "
        >
          <div class="content">
            <div class="title">Risk-Taking Ability</div>
            <div
              class="description"
              v-if="goalRiskProfile.getRiskAbilityResponse"
            >
              {{ goalRiskProfile.getRiskAbilityResponse.riskProfile }}
            </div>
          </div>
        </a>
        <a
          href="#"
          @click.prevent="changeRiskStep('riskTolerance')"
          :class="
            (getRiskToleranceResponse ? 'completed ' : '') +
            ' step ' +
            (riskStep == 'riskTolerance' ? 'active' : '') +
            (goalRiskProfile.getRiskAbilityResponse ? '' : ' disabled')
          "
        >
          <div class="content">
            <div class="title">Loss Tolerance</div>
            <div
              class="description"
              v-if="goalRiskProfile.getRiskToleranceResponse"
            >
              {{ goalRiskProfile.getRiskToleranceResponse.riskProfile }}
            </div>
          </div>
        </a>
        <a
          href="#"
          @click.prevent="changeRiskStep('goalRiskProfile')"
          :class="
            (goalRiskProfile.getRiskToleranceResponse &&
            goalRiskProfile.getRiskAbilityResponse
              ? 'completed '
              : '') +
            'step ' +
            (riskStep == 'goalRiskProfile'
              ? 'active'
              : goalRiskProfile.getRiskToleranceResponse &&
                goalRiskProfile.getRiskAbilityResponse
              ? ''
              : 'disabled')
          "
          :style="
            riskStep == 'goalRiskProfile'
              ? 'font-color: black; background-color: rgba(0, 0, 0, 0.2)'
              : 'font-color: black; '
          "
        >
          <div class="content">
            <div class="title">
              <span v-if="goalRiskProfile.getGoalRiskProfileResponse">
                <span
                  v-if="
                    goalRiskProfile.getGoalRiskProfileResponse.greenYellowRed ==
                    0
                  "
                >
                  <i class="ui icon circle" style="color: green"></i>
                </span>
                <span
                  v-if="
                    goalRiskProfile.getGoalRiskProfileResponse.greenYellowRed ==
                    1
                  "
                >
                  <i class="ui icon circle" style="color: orange"></i>
                </span>
                <span
                  v-if="
                    goalRiskProfile.getGoalRiskProfileResponse.greenYellowRed ==
                    2
                  "
                >
                  <i class="ui icon circle" style="color: red"></i>
                </span> </span
              >Goal Risk Profile (GRP)
            </div>
            <div
              class="description"
              v-if="goalRiskProfile.getGoalRiskProfileResponse"
            >
              {{ goalRiskProfile.getGoalRiskProfileResponse.riskProfile }}
            </div>
          </div></a
        >
      </div>
    </div>

    <div
      class="ui attached segment"
      style="background: rgba(180, 180, 180, 0.2)"
    >
      <div v-if="riskStep == 'riskNeed' && getRiskNeedResponse">
        <div class="ui attached message">
          <div class="ui">
            <strong>1. Risk Need </strong> -
            <i class="ui">
              "the required return needed to grow or preserve current assets to
              fund future goals"
            </i>
          </div>
        </div>
        <div class="ui attached segment">
          <div class="ui message">
            Given the goal's objectives, an estimated
            {{ formatNr(getRiskNeedResponse.requiredRateOfReturn, "0,0.0%") }}
            rate of return is required.

            <div class="ui">
              Risk need is
              <strong>{{ getRiskNeedResponse.riskProfile }}</strong>
            </div>
          </div>
        </div>
        <div
          class="ui attached segment"
          v-if="getRiskNeedResponse.riskProfile == 'Unrealistic'"
        >
          <div class="ui attached warning message">
            <div class="ui header">Decision Point</div>
            <br />
            <div class="ui content">
              <div class="ui inverted orange segment">
                {{ getRiskNeedResponse.message }}
              </div>
            </div>
          </div>
          <div class="ui attached message">
            <div
              class="ui primary button"
              @click.prevent="changeRiskStep('riskAbility')"
            >
              Proceed
            </div>
            <div class="ui button" @click.prevent="changePrimaryStep(3)">
              Stop and Revise Objectives
            </div>
          </div>
        </div>
        <div v-else class="ui attached message">
          <div class="ui content">
            <div
              class="ui primary button"
              @click.prevent="changeRiskStep('riskAbility')"
            >
              Proceed to 2. Risk Ability
            </div>
          </div>
        </div>
      </div>
      <div v-if="riskStep == 'riskAbility'">
        <RiskAbility
          :currency="currency"
          :getRiskAbilityResponse="getRiskAbilityResponse"
          :goalRiskProfile="goalRiskProfile"
          :activePlan="activePlan"
        />

        <div
          v-if="getRiskAbilityResponse"
          class="ui primary button"
          @click.prevent="changeRiskStep('riskTolerance')"
        >
          Proceed to Risk Tolerance
        </div>
      </div>
      <div v-if="riskStep == 'riskTolerance'">
        <RiskTolerance
          :currency="currency"
          :getRiskToleranceResponse="getRiskToleranceResponse"
          :goalRiskProfile="goalRiskProfile"
        />
        <div
          v-if="goalRiskProfile.getGoalRiskProfileResponse.riskProfile"
          class="ui primary button"
          @click.prevent="changeRiskStep('goalRiskProfile')"
        >
          View GRP
        </div>
      </div>
      <div v-if="riskStep == 'goalRiskProfile'">
        <GoalRiskProfile
          :currency="currency"
          :goalRiskProfile="goalRiskProfile"
          :activePlan="activePlan"
        />
        <div
          v-if="goalRiskProfile.getGoalRiskProfileResponse.greenYellowRed == 2"
          class="ui attached message"
        >
          <div class="ui button" @click.prevent="changePrimaryStep(3)">
            Stop and Revise Objectives
          </div>
        </div>
      </div>
    </div>
    <div v-if="false">
      <div class="ui divider"></div>

      <Section class="ui message" style="font-size: 90%">
        <h5>
          <i style="font-size: 85%" class="ui info icon"></i>Background: How is
          a Goal Risk Profile assessed?
        </h5>

        <p>
          We will measure the goal's <br />
          • <strong>1. need for risk</strong> a calculated required RoR will be
          deduced from inputs. <br />
          • <strong>2. ability to take risk</strong> includes the time horizon,
          potential need for liquidity, and risk capacity. These factors will
          determine the financial ability to withstand declines in portfolio
          values. The ability to take risk can often be a limiting factor when
          considering a need for risk to meet corresponding goals. <br />•
          <strong> 3. behavioral loss tolerance</strong> can upset the most
          carefully devised quantitative portfolio strategy. An aversion to any
          kind of risk is - similar to the ability to take risk - a limiting
          factor.
        </p>

        <p>
          <i>Sources:</i> <sup>1</sup> CFA, Investment Risk Profiling: A guide
          for financial advisors. 2020
        </p>
      </Section>
    </div>
  </div>
</template>

<script>
import * as _ from "lodash";
import numeral from "numeral";
import vSelect from "vue-select";
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/default.css";
import "vue-select/dist/vue-select.css";
import RiskTolerance from "@/components/NewGoalTypeRiskToleranceQuestionnaire";
import RiskAbility from "@/components/NewGoalTypeRiskAbility";
import GoalRiskProfile from "@/components/NewGoalTypeGoalRiskProfile";

import {
  getGoalTypes,
  getRiskNeed,
  getRiskAbility,
  getGoalRiskProfile,
} from "@/gateway.js";
import {
  GetProjection,
  GetIncomeCashFlowRequest,
  GetContributionCashFlowRequest,
} from "@/dtos";

export default {
  name: "GoalTypes",
  components: {
    vSelect,
    VueSlider,
    RiskTolerance,
    RiskAbility,
    GoalRiskProfile,
  },
  props: ["currency", "activePlan", "riskStep", "goalCreated", "currency"],
  data() {
    return {
      refreshNeeded: false,
      overrideRiskAbilityProfile: true,
      goalRiskProfile: {
        getRiskAbilityResponse: null,
        getRiskNeedResponse: null,
        getRiskToleranceResponse: null,
        getGoalRiskProfileResponse: null,
        ///    riskProfile: null
      },

      // riskStep: "riskNeed",
      getRiskNeedResponse: null,
      getRiskAbilityResponse: null,
      getRiskToleranceResponse: null,
      getGoalRiskProfileResponse: null,
    };
  },
  watch: {
    getIncomeCashFlowRequest: {
      handler: function (newValue) {
        this.refreshNeeded = true;
      },
      deep: true,
    },
    getContributionCashFlowRequest: {
      handler: function (newValue) {
        this.refreshNeeded = true;
      },
      deep: true,
    },
    goalRiskProfile: {
      handler: function (newValue) {
        this.getRiskNeedResponse = this.goalRiskProfile.getRiskNeedResponse;
        this.getRiskAbilityResponse = this.goalRiskProfile.getRiskAbilityResponse;
        this.getRiskToleranceResponse = this.goalRiskProfile.getRiskToleranceResponse;
        this.getGoalRiskProfileResponse = this.goalRiskProfile.getGoalRiskProfileResponse;
      },
      deep: true,
    },
    getRiskNeedResponse: {
      handler: function (newValue) {
        this.calculateGoalRiskProfile();
      },
      deep: true,
    },
    getRiskToleranceResponse: {
      handler: function (newValue) {
        this.calculateGoalRiskProfile();
      },
      deep: true,
    },
    getRiskAbilityResponse: {
      handler: function (newValue) {
        this.calculateGoalRiskProfile();
      },
      deep: true,
    },
  },
  computed: {
    getIncomeCashFlowRequest() {
      return this.activePlan.getIncomeCashFlowRequest;
    },
    getContributionCashFlowRequest() {
      return this.activePlan.getContributionCashFlowRequest;
    },
  },
  methods: {
    changeRiskStep(event) {
      //if (event == "riskAbility") this.calculateRiskAbility();
      this.$emit("changeRiskStep", { riskStep: event });
    },
    changePrimaryStep(event) {
      this.$emit("changePrimaryStep", {
        step: event,
        goalRiskProfile: this.goalRiskProfile,
      });
    },
    formatNr(val, fmt) {
      return numeral(val).format(fmt ? fmt : "0,0.0a");
    },
    async calculateGoalRiskProfile() {
      if (this.goalRiskProfile) {
        var crncy = this.currency;
        if (!crncy) crncy = "ZAR";
        const response = await getGoalRiskProfile({
          currency: crncy,
          getRiskToleranceResponse: this.goalRiskProfile
            .getRiskToleranceResponse,
          getRiskAbilityResponse: this.goalRiskProfile.getRiskAbilityResponse,
          getRiskNeedResponse: this.goalRiskProfile.getRiskNeedResponse,
        });
        if (response) {
          console.log(
            "emitting - calculateGoalRiskProfile calculated",
            response
          );
          this.$emit("goalRiskProfileChanged", {
            getGoalRiskProfileResponse: response,
          });
          // this.goalRiskProfile = response.goalRiskProfile;
          this.getGoalRiskProfileResponse = response;
          //   this.activePlan.goalRiskProfile = this.goalRiskProfile;
          //  this.activePlan.goalRiskProfile.riskProfileObject =
          //   response.riskProfileObject;
          this.goalRiskProfile.getGoalRiskProfileResponse = response;
          this.activePlan.goalRiskProfile.getGoalRiskProfileResponse = response;
          this.activePlan.goalRiskProfile.getRiskToleranceResponse = this.goalRiskProfile.getRiskToleranceResponse;

          this.activePlan.goalRiskProfile.getRiskNeedResponse = this.goalRiskProfile.getRiskNeedResponse;

          this.activePlan.goalRiskProfile.getRiskAbilityResponse = this.goalRiskProfile.getRiskAbilityResponse;
        }
      }
    },
    async calculateRiskNeed() {
      let getIncomeCashFlowRequestR = new GetIncomeCashFlowRequest({
        income: this.activePlan.getIncomeCashFlowRequest.income,
        incomeGrowth: this.activePlan.getIncomeCashFlowRequest.incomeGrowth,
        incomeGrowthFrequency: this.activePlan.getIncomeCashFlowRequest
          .incomeGrowthFrequency,
        payoutFrequency: this.activePlan.getIncomeCashFlowRequest
          .payoutFrequency,
        incomeToLast: this.activePlan.getIncomeCashFlowRequest.incomeToLast,
        startYear: this.activePlan.getIncomeCashFlowRequest.startYear,
        currentAge: this.activePlan.getIncomeCashFlowRequest.currentAge,

        taxRate: this.activePlan.getIncomeCashFlowRequest.taxRate,
        hasAdhocCashFlow: this.activePlan.getIncomeCashFlowRequest
          .hasAdhocCashFlow,
        adhocCashFlowAge1: this.activePlan.getIncomeCashFlowRequest
          .adhocCashFlowAge1,
        adhocCashFlowAge2: this.activePlan.getIncomeCashFlowRequest
          .adhocCashFlowAge2,
        adhocCashFlowAge3: this.activePlan.getIncomeCashFlowRequest
          .adhocCashFlowAge3,
        adhocCashFlow1: this.activePlan.getIncomeCashFlowRequest.adhocCashFlow1,
        adhocCashFlow2: this.activePlan.getIncomeCashFlowRequest.adhocCashFlow2,
        adhocCashFlow3: this.activePlan.getIncomeCashFlowRequest.adhocCashFlow3,
      });

      let getContributionCashFlowRequestR = new GetContributionCashFlowRequest({
        applyContribution: this.activePlan.getContributionCashFlowRequest
          .applyContribution,
        contribution: this.activePlan.getContributionCashFlowRequest
          .contribution,
        contributionFrequency: this.activePlan.getContributionCashFlowRequest
          .contributionFrequency,
        contributionGrowth:
          this.activePlan.getContributionCashFlowRequest.contributionGrowth /
          100,
        contributionGrowthFrequency: this.activePlan
          .getContributionCashFlowRequest.contributionGrowthFrequency,
        contributionToLast: this.activePlan.getContributionCashFlowRequest
          .contributionToLast,
        contributionStartYear: this.activePlan.getContributionCashFlowRequest
          .contributionStartYear,
      });

      const response = await getRiskNeed({
        currency: this.currency,
        lumpSum: this.activePlan.accounts[0].accountMarketValue,
        getIncomeCashFlowRequest: getIncomeCashFlowRequestR,
        getContributionCashFlowRequest: getContributionCashFlowRequestR,
        goalInfo: this.activePlan.goalInfo,
      });
      if (response) {
        this.getRiskNeedResponse = response;
        this.goalRiskProfile.getRiskNeedResponse = response;
        this.activePlan.goalRiskProfile.getRiskNeedResponse = this.goalRiskProfile.getRiskNeedResponse;
      }
    },
    async calculateRiskAbility() {
      let getIncomeCashFlowRequestR = new GetIncomeCashFlowRequest({
        income: this.activePlan.getIncomeCashFlowRequest.income,
        incomeGrowth: this.activePlan.getIncomeCashFlowRequest.incomeGrowth,
        incomeGrowthFrequency: this.activePlan.getIncomeCashFlowRequest
          .incomeGrowthFrequency,
        payoutFrequency: this.activePlan.getIncomeCashFlowRequest
          .payoutFrequency,
        incomeToLast: this.activePlan.getIncomeCashFlowRequest.incomeToLast,
        startYear: this.activePlan.getIncomeCashFlowRequest.startYear,
        currentAge: this.activePlan.getIncomeCashFlowRequest.currentAge,

        taxRate: this.activePlan.getIncomeCashFlowRequest.taxRate,
        hasAdhocCashFlow: this.activePlan.getIncomeCashFlowRequest
          .hasAdhocCashFlow,
        adhocCashFlowAge1: this.activePlan.getIncomeCashFlowRequest
          .adhocCashFlowAge1,
        adhocCashFlowAge2: this.activePlan.getIncomeCashFlowRequest
          .adhocCashFlowAge2,
        adhocCashFlowAge3: this.activePlan.getIncomeCashFlowRequest
          .adhocCashFlowAge3,
        adhocCashFlow1: this.activePlan.getIncomeCashFlowRequest.adhocCashFlow1,
        adhocCashFlow2: this.activePlan.getIncomeCashFlowRequest.adhocCashFlow2,
        adhocCashFlow3: this.activePlan.getIncomeCashFlowRequest.adhocCashFlow3,
      });

      let getContributionCashFlowRequestR = new GetContributionCashFlowRequest({
        applyContribution: this.activePlan.getContributionCashFlowRequest
          .applyContribution,
        contribution: this.activePlan.getContributionCashFlowRequest
          .contribution,
        contributionFrequency: this.activePlan.getContributionCashFlowRequest
          .contributionFrequency,
        contributionGrowth:
          this.activePlan.getContributionCashFlowRequest.contributionGrowth /
          100,
        contributionGrowthFrequency: this.activePlan
          .getContributionCashFlowRequest.contributionGrowthFrequency,
        contributionToLast: this.activePlan.getContributionCashFlowRequest
          .contributionToLast,
        contributionStartYear: this.activePlan.getContributionCashFlowRequest
          .contributionStartYear,
      });

      const response = await getRiskAbility({
        currency: this.currency,
        goal: this.activePlan.goalInfo,
        lumpSum: this.activePlan.accounts[0].accountMarketValue,
        getIncomeCashFlowRequest: getIncomeCashFlowRequestR,
        getContributionCashFlowRequest: getContributionCashFlowRequestR,
      });
      if (response) {
        console.log("getRiskAbility calculated", response);
        this.getRiskAbilityResponse = response;
        this.goalRiskProfile.getRiskAbilityResponse = response;
        this.activePlan.goalRiskProfile.getRiskAbilityResponse = this.goalRiskProfile.getRiskAbilityResponse;

        console.log("this.goalRiskProfile", this.goalRiskProfile);
      }
    },
  },
  mounted() {
    //this.calculateRiskAbility();
    //console.log(this.goalCreated, "goalCreated");
    if (this.goalCreated) {
      //console.log("this goal created so trying to assign responses");
      this.getRiskNeedResponse = this.activePlan.goalRiskProfile.getRiskNeedResponse;
      this.getRiskToleranceResponse = this.activePlan.goalRiskProfile.getRiskToleranceResponse;
      this.getRiskAbilityResponse = this.activePlan.goalRiskProfile.getRiskAbilityResponse;
      this.getGoalRiskProfileResponse = this.activePlan.goalRiskProfile.getGoalRiskProfileResponse;
      this.goalRiskProfile = this.activePlan.goalRiskProfile;
    } else {
      this.calculateRiskNeed();
    }
  },
};
</script>
<style scoped>
.selecthover tr {
  cursor: pointer;
}
</style>
